"use client";

import { useEffect, useId, useRef, useState, type Ref } from "react";
import type { OwnPortalReview, PortalReview, PortalReviewsResponse } from "../lib/clientPortalApi";
import { PortalReviewRating as Stars, PortalReviewStar as StarIcon } from "./PortalReviewRating";
import { PortalReviewFilters } from "./PortalReviewFilters";
import { PortalReviewComposer, type PortalReviewDraft } from "./PortalReviewComposer";
import styles from "./PortalReviews.module.css";

const pageSize = 5;
const statusLabels: Record<OwnPortalReview["status"], string> = {
  pending: "Čeká na schválení",
  approved: "Zveřejněno",
  rejected: "Nezveřejněno",
  hidden: "Skryto",
};

function PencilIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 5 4 4M4 20l4.5-1L20 7.5a2.8 2.8 0 0 0-4-4L4.5 15Z" /></svg>;
}

function ReviewCard({ review, ownReview, disabled, editRef, onEdit }: {
  review: PortalReview;
  ownReview?: OwnPortalReview;
  disabled?: boolean;
  editRef?: Ref<HTMLButtonElement>;
  onEdit?: () => void;
}) {
  const date = new Date(review.createdAt);
  return <article className={styles.review}>
    <header className={styles.reviewHeader}>
      <div className={styles.author}>
        <span className={styles.avatar} aria-hidden="true">{review.authorName.slice(0, 1).toUpperCase()}</span>
        <div><strong>{review.authorName}</strong>{!Number.isNaN(date.getTime()) ? <time dateTime={review.createdAt}>{new Intl.DateTimeFormat("cs", { dateStyle: "medium" }).format(date)}</time> : null}</div>
      </div>
      <div className={styles.reviewActions}>
        <Stars rating={review.rating} />
        {onEdit ? <button ref={editRef} type="button" className={styles.editButton} disabled={disabled} onClick={onEdit} aria-label="Upravit recenzi" title="Upravit recenzi"><PencilIcon /></button> : null}
      </div>
    </header>
    {review.title ? <h3>{review.title}</h3> : null}
    <p className={styles.reviewBody}>{review.body}</p>
    {ownReview ? <div className={styles.reviewMeta}><span className={styles.status} data-status={ownReview.status}>{statusLabels[ownReview.status]}</span></div> : null}
    {ownReview?.moderatorReply ? <div className={styles.reply}><strong>Odpověď poskytovatele</strong><p>{ownReview.moderatorReply}</p></div> : null}
  </article>;
}

export function PortalReviews({ portalSlug }: { portalSlug: string }) {
  const id = useId();
  const [data, setData] = useState<PortalReviewsResponse | null>(null);
  const [page, setPage] = useState(0);
  const [selectedRatings, setSelectedRatings] = useState<number[]>([]);
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [view, setView] = useState<"all" | "mine">("all");
  const [editing, setEditing] = useState(false);
  const submitting = useRef(false);
  const listHeadingRef = useRef<HTMLHeadingElement>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const restoreEditFocus = useRef(false);
  const focusResults = useRef(false);
  const endpoint = `/api/client-portals/${encodeURIComponent(portalSlug)}/reviews`;
  const ratingsQuery = selectedRatings.join(",");
  const canReview = data?.canReview === true;
  const anonymous = data?.canReviewAnonymously === true;

  useEffect(() => {
    if (!loading && !editing && restoreEditFocus.current) {
      restoreEditFocus.current = false;
      editButtonRef.current?.focus({ preventScroll: true });
    }
  }, [loading, editing, view]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(null);
    const query = new URLSearchParams({ limit: String(pageSize), offset: String(page * pageSize) });
    if (ratingsQuery) query.set("ratings", ratingsQuery);
    fetch(`${endpoint}?${query}`, { signal: controller.signal, cache: "no-store" })
      .then(async (response) => {
        if (!response.ok) {
          if (response.status === 401 || response.status === 403 || response.status === 404) {
            if (!controller.signal.aborted) { setData(null); setEditing(false); }
            throw new Error("Recenze nejsou dostupné. Ověřte přihlášení a přístup do této části portálu.");
          }
          throw new Error("Recenze se nepodařilo načíst. Zkuste to znovu.");
        }
        return response.json() as Promise<PortalReviewsResponse>;
      })
      .then((result) => {
        if (controller.signal.aborted) return;
        const lastPage = Math.max(0, Math.ceil(result.filteredTotal / pageSize) - 1);
        if (page > lastPage) { setPage(lastPage); return; }
        setData(result);
        if (focusResults.current) {
          focusResults.current = false;
          listHeadingRef.current?.focus({ preventScroll: true });
          listHeadingRef.current?.scrollIntoView({ block: "nearest" });
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) setLoadError(error instanceof Error ? error.message : "Recenze se nepodařilo načíst.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [endpoint, page, ratingsQuery, revision]);

  function filterRatings(next: number[]) {
    setLoading(true);
    setSelectedRatings([...next].sort((a, b) => b - a));
    setPage(0);
  }

  function changePage(next: number) {
    focusResults.current = true;
    setLoading(true);
    setPage(next);
  }

  async function save(draft: PortalReviewDraft) {
    if (submitting.current || loading || !canReview) return;
    submitting.current = true;
    setSaving(true);
    setSaveError(null);
    setNotice(null);
    try {
      const response = await fetch(endpoint, { method: anonymous ? "POST" : "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify(draft) });
      if (!response.ok) {
        throw new Error(response.status === 401 || response.status === 403
          ? "Pro odeslání hodnocení se přihlaste účtem s přístupem k recenzím."
          : response.status === 429
            ? "Hodnocení jste odeslali příliš často. Chvíli počkejte a zkuste to znovu."
            : response.status === 422
              ? "Zkontrolujte počet hvězdiček a vyplňte text recenze."
              : "Hodnocení se nepodařilo uložit. Váš text zůstal zachovaný, zkuste to znovu.");
      }
      const ownReview = await response.json() as OwnPortalReview;
      restoreEditFocus.current = !anonymous;
      setEditing(false);
      setView(anonymous ? "all" : "mine");
      if (!anonymous) setData((current) => current ? { ...current, ownReview } : current);
      setNotice(ownReview.status === "pending" ? "Děkujeme za hodnocení. Recenzi jsme přijali ke schválení." : "Vaše hodnocení je uložené.");
      setLoading(true);
      setPage(0);
      setRevision((value) => value + 1);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Hodnocení se nepodařilo uložit.");
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  }

  const pageCount = Math.max(1, Math.ceil((data?.filteredTotal ?? 0) / pageSize));
  const firstPageNumber = Math.max(0, Math.min(page - 2, pageCount - 5));
  const pageNumbers = Array.from({ length: Math.min(pageCount, 5) }, (_, index) => firstPageNumber + index);

  const ownReview = anonymous ? null : data?.ownReview ?? null;
  const showingMine = canReview && !anonymous && view === "mine";

  function cancelEdit() {
    restoreEditFocus.current = true;
    setEditing(false);
    setSaveError(null);
  }

  return <div className={styles.container}>
    {notice ? <p className={styles.success} role="status">{notice}</p> : null}
    {!data && loadError ? <div className={styles.loadError} role="alert"><p>{loadError}</p><button type="button" className="secondary-button" onClick={() => setRevision((value) => value + 1)}>Zkusit znovu</button></div> : null}
    {loading && !data ? <div className={styles.loading} role="status"><span className={styles.loadingDot} aria-hidden="true" />Načítání recenzí…</div> : null}
    {data ? <>
      {canReview && !anonymous ? <div className={styles.viewSwitcher} role="group" aria-label="Zobrazení recenzí">
        <button type="button" aria-pressed={!showingMine} aria-controls={`${id}-list`} disabled={saving} onClick={() => setView("all")}>Všechna hodnocení</button>
        <button type="button" aria-pressed={showingMine} aria-controls={`${id}-list`} disabled={saving} onClick={() => setView("mine")}>Moje hodnocení</button>
      </div> : null}
      {!showingMine ? <PortalReviewFilters averageRating={data.averageRating} total={data.total} ratingCounts={data.ratingCounts} selectedRatings={selectedRatings} disabled={loading || saving} onChange={filterRatings} /> : null}
      <section id={`${id}-list`} className={styles.list} aria-labelledby={`${id}-list-heading`} aria-busy={loading}>
        <h2 ref={listHeadingRef} id={`${id}-list-heading`} className={styles.visuallyHidden} tabIndex={-1}>{showingMine ? "Moje hodnocení" : "Recenze klientů"}</h2>
        {loadError ? <div className={styles.loadError} role="alert"><p>{loadError}</p><button type="button" className="secondary-button" onClick={() => setRevision((value) => value + 1)}>Zkusit znovu</button></div>
          : showingMine ? ownReview && !editing ? <ReviewCard review={ownReview} ownReview={ownReview} disabled={loading || saving} editRef={editButtonRef} onEdit={() => { setSaveError(null); setEditing(true); }} /> : null
          : <>
            <div className={styles.listMeta}><span aria-live="polite">{selectedRatings.length ? `${data.filteredTotal} z ${data.total}` : ""}</span><span>Od nejnovějších</span></div>
            <div className={styles.results} data-loading={loading}>
              <div className={styles.resultsContent} inert={loading} aria-hidden={loading || undefined}>
                {data.items.length === 0 ? <div className={styles.empty}>
                  <span className={styles.emptyIcon} aria-hidden="true"><StarIcon /></span>
                  <p>{selectedRatings.length ? "Vybranému filtru neodpovídá žádná recenze." : "Zatím tu nejsou žádné zveřejněné recenze."}</p>
                  {selectedRatings.length ? <button type="button" className="secondary-button" onClick={() => filterRatings([])}>Zobrazit všechna hodnocení</button> : null}
                </div> : <div className={styles.reviewStack}>{data.items.map((review, index) => <ReviewCard key={`${page}-${ratingsQuery}-${index}-${review.createdAt}`} review={review} />)}</div>}
              </div>
              {loading ? <div className={styles.loadingOverlay} role="status"><span className={styles.loadingDot} aria-hidden="true" />Načítání recenzí…</div> : null}
            </div>
          </>}
        {!showingMine && data.filteredTotal > pageSize && !loadError ? <nav className={styles.pagination} aria-label="Stránky recenzí">
          <span>{page * pageSize + 1}–{Math.min((page + 1) * pageSize, data.filteredTotal)} z {data.filteredTotal}</span>
          <div className={styles.pageButtons}>
            <button type="button" disabled={page === 0 || loading || saving} onClick={() => changePage(page - 1)} aria-label="Předchozí stránka"><span aria-hidden="true">←</span></button>
            {pageNumbers.map((number) => <button key={number} type="button" aria-label={`Strana ${number + 1}`} aria-current={number === page ? "page" : undefined} disabled={loading || saving} onClick={() => { if (number !== page) changePage(number); }}>{number + 1}</button>)}
            <button type="button" disabled={page >= pageCount - 1 || loading || saving} onClick={() => changePage(page + 1)} aria-label="Další stránka"><span aria-hidden="true">→</span></button>
          </div>
        </nav> : null}
      </section>
      {canReview ? <PortalReviewComposer
        key={`${ownReview?.uuid ?? "new"}:${ownReview?.updatedAt ?? ""}:${anonymous ? revision : "member"}`}
        ownReview={ownReview}
        anonymous={anonymous}
        active={!ownReview || (showingMine && editing)}
        saving={saving}
        disabled={saving || loading || Boolean(loadError)}
        error={saveError}
        onSubmit={save}
        onCancel={cancelEdit}
      /> : null}
    </> : null}
  </div>;
}
